
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Check, CheckCircle2, ChevronDown, Globe, MapPinned, MessageCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CategoryResult } from "@/types/supabase";
import { cn } from "@/lib/utils";
import {
  areaPillClassName,
  areaPillRemoveClassName,
  categoryPillClassName,
  categoryPillRemoveClassName,
} from "@/components/shared/selectionPillStyles";

interface FormData {
  name: string;
  categories: string[];
  description: string;
  websiteUrl: string;
  suburbs: string[];
  imageUrl: string;
  isFeatured: boolean;
  isVerified: boolean;
  whatsappNumber: string;
}

interface AdminCreateListingFormProps {
  formData: FormData;
  categories: CategoryResult[];
  areasGrouped: Record<string, string[]>;
  isDisabled: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onFieldChange: (field: string, value: unknown) => void;
  onToggle: (field: string) => void;
  onCategorySelect: (categoryName: string) => void;
  onCategoryRemove: (category: string) => void;
  onSuburbAdd: (suburb: string) => void;
  onSuburbRemove: (suburb: string) => void;
}

const AdminCreateListingForm = ({
  formData,
  categories,
  areasGrouped,
  isDisabled,
  onSubmit,
  onFieldChange,
  onToggle,
  onCategorySelect,
  onCategoryRemove,
  onSuburbAdd,
  onSuburbRemove,
}: AdminCreateListingFormProps) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <form onSubmit={onSubmit}>
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Service Name *
          </label>
          <input
            type="text"
            id="name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp focus:border-transparent"
            value={formData.name}
            onChange={(e) => onFieldChange("name", e.target.value)}
            required
            disabled={isDisabled}
          />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categories * (Limited to 1 for unassigned listings)
          </label>
          <div className="mb-2">
            <Select key={formData.categories.length} onValueChange={onCategorySelect} disabled={isDisabled}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select categories" />
              </SelectTrigger>
              <SelectContent>
                {categories
                  .filter(category => !formData.categories.includes(category.name))
                  .map((category) => (
                    <SelectItem key={category.id} value={category.name}>
                      {category.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.categories.length > 0 ? (
              formData.categories.map((category) => (
                <Badge key={category} variant="secondary" className={categoryPillClassName}>
                  {category}
                  <button
                    type="button"
                    onClick={() => onCategoryRemove(category)}
                    className={`ml-1 ${categoryPillRemoveClassName}`}
                    disabled={isDisabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500">Please select at least one category</p>
            )}
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Areas Serviced (Limited to 2 for unassigned listings)
          </label>
          <div className="mb-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between" disabled={isDisabled}>
                  <span className="flex items-center gap-2 truncate">
                    <MapPinned className="h-4 w-4 text-muted-foreground" />
                    {formData.suburbs.length > 0 ? `${formData.suburbs.length} area${formData.suburbs.length === 1 ? "" : "s"} selected` : "Search and select areas"}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0 bg-white shadow-lg border z-50" align="start">
                <Command>
                  <CommandInput placeholder="Search territories or areas..." />
                  <CommandList>
                    <CommandEmpty>No matching areas found.</CommandEmpty>
                    {Object.entries(areasGrouped).map(([territory, suburbs]) => (
                      <CommandGroup key={territory} heading={territory}>
                        {suburbs.map((suburb) => {
                          const isSelected = formData.suburbs.includes(suburb);
                          const isDisabledItem = !isSelected && formData.suburbs.length >= 2;

                          return (
                            <CommandItem
                              key={`${territory}-${suburb}`}
                              value={`${suburb} ${territory}`}
                              onSelect={() => {
                                if (isSelected) {
                                  onSuburbRemove(suburb);
                                } else if (!isDisabledItem) {
                                  onSuburbAdd(suburb);
                                }
                              }}
                              disabled={isDisabled || isDisabledItem}
                              className={cn(isSelected && "text-whatsapp font-medium")}
                            >
                              <CheckCircle2 className={cn("mr-2 h-4 w-4", isSelected ? "text-whatsapp opacity-100" : "opacity-0")} />
                              <span className={cn(isDisabledItem && "text-gray-400")}>{suburb}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.suburbs.length > 0 ? (
              formData.suburbs.map((suburb) => (
                <Badge key={suburb} variant="secondary" className={areaPillClassName}>
                  {suburb}
                  <button
                    type="button"
                    onClick={() => onSuburbRemove(suburb)}
                    className={`ml-1 ${areaPillRemoveClassName}`}
                    disabled={isDisabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500">No areas selected</p>
            )}
          </div>
          <p className="text-sm text-gray-500">Unassigned listings can only cover up to 2 areas.</p>
        </div>
        
        <div className="mb-6">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp focus:border-transparent"
            value={formData.description}
            onChange={(e) => onFieldChange("description", e.target.value)}
            required
            disabled={isDisabled}
          />
        </div>

        <div className="mb-6">
          <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
            <Globe className="h-4 w-4 mr-2" /> Website URL (Optional)
          </label>
          <Input
            type="text"
            id="websiteUrl"
            value={formData.websiteUrl || ""}
            onChange={(e) => onFieldChange("websiteUrl", e.target.value)}
            placeholder="https://www.example.com"
            className="w-full"
            disabled={isDisabled}
          />
          <p className="text-sm text-gray-500 mt-1">
            Link to the service provider's website or portfolio.
          </p>
        </div>

        {/* Listing Features Section */}
        <div className="mb-6 bg-gray-50 rounded-lg p-4 border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Listing Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant={formData.isFeatured ? "default" : "outline"}
                className={`w-full ${formData.isFeatured ? "bg-whatsapp hover:bg-whatsapp-dark" : ""}`}
                onClick={() => onToggle("isFeatured")}
                disabled={isDisabled}
              >
                {formData.isFeatured ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Featured
                  </>
                ) : (
                  "Set as Featured"
                )}
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant={formData.isVerified ? "default" : "outline"}
                className={`w-full ${formData.isVerified ? "bg-green-600 hover:bg-green-700" : ""}`}
                onClick={() => onToggle("isVerified")}
                disabled={isDisabled}
              >
                {formData.isVerified ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Verified
                  </>
                ) : (
                  "Mark as Verified"
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="mb-6 rounded-lg border border-whatsapp/20 bg-whatsapp-light/40 p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information (For Unassigned Listings)</h3>
          <div className="border-t border-whatsapp/20 pt-4">
            <div>
              <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Number
              </label>
              <Input
                type="text"
                id="whatsappNumber"
                value={formData.whatsappNumber || ""}
                onChange={(e) => onFieldChange("whatsappNumber", e.target.value)}
                placeholder="+1234567890"
                className="w-full"
                disabled={isDisabled}
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              WhatsApp contact information for unassigned listings that haven't been claimed by a provider yet.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateListingForm;
